var $ = require("jquery");
import { gsap, ScrollTrigger } from "./globals.js";
import { animateChars } from './animate-chars.js';

gsap.registerPlugin(ScrollTrigger);

const isMobile = window.innerWidth < 1025;

document.addEventListener("DOMContentLoaded", () => {
	const splash = document.querySelector(".hp-splash");
	if (!splash) return;

	const videoWrapper = document.querySelector(".hp-splash__video-wrapper");
	const cursor = videoWrapper.querySelector(".cursor--video");

	const player = document.querySelector(".player");
	const playerMask = player.querySelector(".player__mask");
	const playerInner = player.querySelector(".player__inner");
	const playerCloser = player.querySelector(".closer");
	const playerVideo = player.querySelector("video");

	const state = {
		isOpened: false,
		isAnimating: false
	};

	function openPlayer() {
		if (state.isAnimating || state.isOpened) return;

		state.isAnimating = true;
		state.isOpened = true;

		playerVideo.volume = 0;
		playerVideo.currentTime = 0;
		playerVideo.play();

		gsap.set(player, {
			display: "block",
			pointerEvents: "all"
		});

		gsap.to(playerMask, {
			opacity: 1,
			duration: 1.5,
			ease: "power2.out"
		});

		gsap.to(playerInner, {
			y: "0%",
			duration: 1.5,
			ease: "expo.inOut"
		});

		gsap.to(playerVideo, {
			volume: 1,
			duration: 1.5,
			ease: "power2.out"
		});

		gsap.to(playerCloser, {
			opacity: 1,
			duration: 0.6,
			ease: "power2.out",
			delay: 0.6
		});

		gsap.delayedCall(1.5, () => {
			state.isAnimating = false;
			document.body.style.overflow = "hidden";
		});
	}

	function closePlayer() {
		if (state.isAnimating || !state.isOpened) return;

		state.isAnimating = true;
		state.isOpened = false;

		gsap.to(playerCloser, {
			opacity: 0.001,
			duration: 0.6,
			ease: "power2.out"
		});

		gsap.to(playerMask, {
			opacity: 0.001,
			duration: 1.5,
			ease: "power2.out"
		});

		gsap.to(playerInner, {
			y: "-100%",
			duration: 1.5,
			ease: "expo.inOut"
		});

		gsap.to(playerVideo, {
			volume: 0,
			duration: 1.5,
			ease: "power2.out"
		});

		gsap.delayedCall(1.5, () => {
			playerVideo.pause();
			gsap.set(player, {
				display: "none",
				pointerEvents: "none"
			});
			document.body.style.overflow = "";
			state.isAnimating = false;
		});
	}

	videoWrapper.addEventListener("click", openPlayer);
	playerMask.addEventListener("click", closePlayer);
	playerCloser.addEventListener("click", closePlayer);

	// Timeline 1 (SVG paths)
	const tl1 = gsap.timeline();
	const paths = document.querySelectorAll('.hp-splash svg path');
	const animationDuration = 0.5;
	const overlapDuration = 0.5;

	paths.forEach((path, index) => {
		tl1.fromTo(path, {
			scaleX: 0,
			scaleY: 0,
			transformOrigin: "center",
			opacity: 0,
		}, {
			duration: animationDuration,
			scaleX: 1.1,
			scaleY: 1.1,
			opacity: 1,
			ease: "power1.out",
			delay: index === 0 ? 1.5 : 0
		}, `-=${overlapDuration}`);

		tl1.to(path, {
			duration: 0.2,
			scaleX: 1,
			scaleY: 1,
			ease: "power1.inOut",
		});
	});

	tl1.add(animateChars(".hp-splash__cutout"), "-=1");

	if (!isMobile) {
		tl1.to(".hp-splash__cutout .nav", {
			duration: 1,
			opacity: 1,
			y: 0,
			ease: "power1.inOut"
		}, "-=1.5");
	}

	// ScrollTrigger setup (prepared early)
	let tl2, st2;

	if (!isMobile) {
		tl2 = gsap.timeline({ paused: true });

		tl2.to(".hp-splash__cutout__bottom", {
			opacity: 0,
			ease: "power1.out",
			duration: 0.15
		});

		tl2.to(".hp-splash__cutout__wrap", {
			scale: 5.5,
			x: "-75%",
			y: "45%",
			...(window.innerWidth < 650 && { opacity: 0 }),
			ease: "power2.inOut",
			duration: 0.25
		}, 0);

		st2 = ScrollTrigger.create({
			trigger: ".hp-splash",
			start: "top top",
			end: "+=800vh",
			scrub: 1.5,
			pin: true,
			anticipatePin: 1,
			once: true,
			animation: tl2,
			enabled: false,
			onLeave: self => {
				const scrollY = window.scrollY;

				// Lock scroll
				document.body.style.position = "fixed";
				document.body.style.top = `-${scrollY}px`;
				document.body.style.width = "100%";

				// Wait for styles to apply
				requestAnimationFrame(() => {
					window.scrollTo(0, 0);

					requestAnimationFrame(() => {
						// Clear any styles GSAP pinned on .hp-splash
						gsap.set(".hp-splash", { clearProps: "all" });
						gsap.set(".hp-splash__cutout", { opacity: 0, pointerEvents: "none" });
						document.body.classList.add("cutout-animation-complete");
						document.querySelector(".hp-splash__cutout")?.classList.add("hidden");

						// Kill ScrollTrigger first
						self.kill();

						// Now unwrap .hp-splash from its .pin-spacer container to remove extra padding
						const spacer = document.querySelector(".pin-spacer");
						const splash = document.querySelector(".hp-splash");
						if (spacer && splash && spacer.contains(splash)) {
							spacer.replaceWith(...spacer.childNodes);
						}

						// Restore scroll
						document.body.style.position = "";
						document.body.style.top = "";
						document.body.style.width = "";
						window.scrollTo(0, 0);

						setTimeout(() => {
							ScrollTrigger.refresh();
						}, 50);

						// Re-init ScrollTrigger for the next animation
						ScrollTrigger.create({
							trigger: ".hp-splash__video-wrapper",
							start: "top top",
							end: "bottom top",
							scrub: true,
							animation: gsap.to(".hp-splash__video", {
								y: "70%",
								ease: "none"
							})
						});
					});
				});
			}
		});
	}

	tl1.eventCallback("onComplete", () => {
		if (isMobile) {
			const tlMobile = gsap.timeline();

			tlMobile.to(".hp-splash__cutout__bottom", {
				opacity: 0,
				duration: 0.5,
				ease: "power1.out"
			});

			tlMobile.to({}, {
				duration: 0.01,
				onComplete: () => {
					document.body.classList.add('cutout-animation-complete');
				}
			});

			tlMobile.to(".hp-splash__cutout__wrap", {
				scale: 5.5,
				x: '-75%',
				y: '45%',
				opacity: 0,
				duration: 1,
				ease: "power2.inOut",
				onComplete: () => {
					document.querySelector('.hp-splash__cutout')?.classList.add('hidden');
					document.body.classList.add('loaded');
				}
			}, 0.2);
		} else {
			if (st2) st2.enable();
			document.body.classList.add('loaded');
		}
	});
});
